import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Grid,
  Rating,
  Skeleton,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  GraduationCap,
  BookOpen,
  Award,
  Rocket,
  Brain,
  Target,
  Cpu,
  Leaf,
  Wrench,
  Code,
  ArrowRight,
  Clock,
  Users,
  Microscope,
  BarChart3,
  FlaskConical,
  Layers,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import './Academy.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

// ─── Category Pills ──────────────────────────────────────────────────────────
const CATEGORIES = [
  { label: 'All', icon: <Layers size={14} />, value: '' },
  { label: 'Data Science', icon: <BarChart3 size={14} />, value: 'data-science' },
  { label: 'AI & ML', icon: <Brain size={14} />, value: 'ai-ml' },
  { label: 'Green Tech', icon: <Leaf size={14} />, value: 'green-tech' },
  { label: 'Medical', icon: <Microscope size={14} />, value: 'medical' },
  { label: 'Process Engineering', icon: <Wrench size={14} />, value: 'process-eng' },
  { label: 'Programming', icon: <Code size={14} />, value: 'programming' },
  { label: 'Cloud & DevOps', icon: <Cpu size={14} />, value: 'cloud' },
  { label: 'Virtual Labs', icon: <FlaskConical size={14} />, value: 'virtual-labs' },
];

// ─── Course Card ─────────────────────────────────────────────────────────────
// Subtle, category-matched dark tones — no rainbow gradients
const CATEGORY_COLORS = {
  'ai-ml':        { bg: 'rgba(124,58,237,0.15)', border: 'rgba(124,58,237,0.3)', accent: '#a855f7' },
  'data-science': { bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.3)', accent: '#60a5fa' },
  'green-tech':   { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.3)', accent: '#34d399' },
  'medical':      { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)',  accent: '#f87171' },
  'process-eng':  { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', accent: '#fbbf24' },
  'programming':  { bg: 'rgba(99,102,241,0.15)', border: 'rgba(99,102,241,0.3)', accent: '#818cf8' },
  'cloud':        { bg: 'rgba(6,182,212,0.12)',  border: 'rgba(6,182,212,0.3)',  accent: '#22d3ee' },
  default:        { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)', accent: '#a1a1aa' },
};

const getCategoryStyle = (category = '') => {
  const key = category?.toLowerCase().replace(/[^a-z-]/g, '').replace('/', '-');
  return CATEGORY_COLORS[key] || CATEGORY_COLORS.default;
};

const CourseCard = ({ course }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const style = getCategoryStyle(course.category);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: '14px',
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: 'rgba(255,255,255,0.025)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: style.border,
          boxShadow: `0 12px 40px rgba(0,0,0,0.35)`,
        },
      }}
      onClick={() => navigate('/courses')}
    >
      {/* Thumbnail */}
      {course.thumbnail ? (
        <CardMedia component="img" height={160} image={course.thumbnail} alt={course.title} sx={{ objectFit: 'cover' }} />
      ) : (
        <Box
          sx={{
            height: 160,
            bgcolor: style.bg,
            borderBottom: `1px solid ${style.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <BookOpen size={44} color={style.accent} strokeWidth={1.5} style={{ opacity: 0.5 }} />
          {course.category && (
            <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
              <Chip
                label={course.category}
                size="small"
                sx={{
                  bgcolor: 'rgba(0,0,0,0.4)',
                  color: style.accent,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                  border: `1px solid ${style.border}`,
                }}
              />
            </Box>
          )}
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: '0.95rem',
            mb: 0.75,
            lineHeight: 1.45,
            color: '#fafafa',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {course.title || 'Untitled Course'}
        </Typography>

        <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 1.5, fontWeight: 500 }}>
          {course.instructor?.name || course.instructorName || 'QoreNova Instructor'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
          <Rating value={course.rating || 0} precision={0.5} size="small" readOnly sx={{ fontSize: '0.85rem' }} />
          {course.reviewCount > 0 && (
            <Typography variant="caption" sx={{ color: '#71717a' }}>({course.reviewCount})</Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {course.enrollmentCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Users size={12} color="#52525b" />
              <Typography variant="caption" sx={{ color: '#71717a' }}>
                {course.enrollmentCount.toLocaleString()} {t('academyPage.enrolledLabel')}
              </Typography>
            </Box>
          )}
          {course.duration && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Clock size={12} color="#52525b" />
              <Typography variant="caption" sx={{ color: '#71717a' }}>{course.duration}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {course.price === 0 || course.isFree ? (
            <Chip label={t('academyPage.freeLabel')} size="small" sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#34d399', fontWeight: 700, fontSize: '0.75rem', border: '1px solid rgba(16,185,129,0.25)' }} />
          ) : course.price ? (
            <Typography variant="body2" sx={{ fontWeight: 800, color: style.accent }}>${course.price}</Typography>
          ) : null}
        </Box>
        <Button
          size="small"
          endIcon={<ArrowRight size={13} />}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            py: 0.6,
            px: 1.5,
            color: '#a1a1aa',
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': { color: '#fafafa', borderColor: 'rgba(255,255,255,0.25)', bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          {t('academyPage.viewBtn')}
        </Button>
      </CardActions>
    </Card>
  );
};

// ─── Skeleton Card ────────────────────────────────────────────────────────────
const CourseCardSkeleton = () => (
  <Card elevation={0} sx={{ borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
    <Skeleton variant="rectangular" height={160} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
    <CardContent>
      <Skeleton variant="text" height={24} sx={{ bgcolor: 'rgba(255,255,255,0.06)', mb: 1 }} />
      <Skeleton variant="text" height={18} width="55%" sx={{ bgcolor: 'rgba(255,255,255,0.04)', mb: 1.5 }} />
      <Skeleton variant="text" height={14} width="35%" sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
    </CardContent>
    <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', justifyContent: 'space-between' }}>
      <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 4, bgcolor: 'rgba(255,255,255,0.04)' }} />
      <Skeleton variant="rectangular" width={70} height={28} sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)' }} />
    </Box>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Academy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');

  useEffect(() => {
    document.documentElement.classList.add('qn-home-html');
    document.body.classList.add('qn-home-body');
    return () => {
      document.documentElement.classList.remove('qn-home-html');
      document.body.classList.remove('qn-home-body');
    };
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      setCoursesLoading(true);
      try {
        const params = activeCategory ? `?category=${activeCategory}` : '';
        const res = await fetch(`${API_BASE}/api/courses${params}`);
        const data = await res.json().catch(() => ({}));
        const courseList = Array.isArray(data) ? data : (data?.courses || data?.data || []);
        setCourses(courseList.slice(0, 12));
      } catch {
        setCourses([]);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, [activeCategory]);

  const featured = courses.slice(0, 6);

  return (
    <Box className="ac" sx={{ minHeight: '100vh', bgcolor: '#05080f' }}>
      <Header />

      {/* ── WHAT WE OFFER ────────────────────────────────────────────────── */}
      <Box className="ac-lead" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }}>
            <Box sx={{ mb: 7 }}>
              <Typography variant="overline" sx={{ color: '#fbbf24', fontWeight: 800, letterSpacing: '0.14em', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                {t('academyPage.platformLabel')}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#fafafa', mb: 1.5, letterSpacing: '-0.03em', fontSize: { xs: '1.8rem', md: '2.35rem' } }}>
                {t('academyPage.platformTitle')}
              </Typography>
              <Typography sx={{ color: '#94a3b8', maxWidth: 460, fontSize: '0.98rem', lineHeight: 1.6 }}>
                {t('academyPage.platformSubtitle')}
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {[
              { icon: <BookOpen size={22} />, title: t('academyPage.featureCoursesTitle'), desc: t('academyPage.featureCoursesDesc'), href: '/courses', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
              { icon: <Brain size={22} />, title: t('academyPage.featureAITutorTitle'), desc: t('academyPage.featureAITutorDesc'), href: '/aitutoring', color: '#fb7185', bg: 'rgba(244,63,94,0.12)' },
              { icon: <FlaskConical size={22} />, title: t('academyPage.featureVirtualLabsTitle'), desc: t('academyPage.featureVirtualLabsDesc'), href: '/virtuallabs', color: '#22d3ee', bg: 'rgba(34,211,238,0.12)' },
              { icon: <Target size={22} />, title: t('academyPage.featurePathsTitle'), desc: t('academyPage.featurePathsDesc'), href: '/careerpaths', color: '#fbbf24', bg: 'rgba(245,158,11,0.1)' },
              { icon: <Award size={22} />, title: t('academyPage.featureCertsTitle'), desc: t('academyPage.featureCertsDesc'), href: '/certifications', color: '#fb7185', bg: 'rgba(244,63,94,0.1)' },
              { icon: <Zap size={22} />, title: t('academyPage.featureCoachingTitle'), desc: t('academyPage.featureCoachingDesc'), href: '/coachingmentoring', color: '#22d3ee', bg: 'rgba(34,211,238,0.1)' },
            ].map((f, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    elevation={0}
                    onClick={() => navigate(f.href)}
                    sx={{
                      height: '100%', p: 3, borderRadius: '14px', cursor: 'pointer',
                      bgcolor: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-3px)', borderColor: `${f.color}40`, boxShadow: `0 8px 30px ${f.color}14` },
                    }}
                  >
                    <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: f.color, mb: 2.5 }}>
                      {f.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.75, color: '#fafafa' }}>{f.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#71717a', lineHeight: 1.65, mb: 2.5, fontSize: '0.875rem' }}>{f.desc}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: f.color }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{t('academyPage.featureExplore')}</Typography>
                      <ArrowRight size={13} />
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── BROWSE COURSES ───────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          {/* Section header */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 5, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="overline" sx={{ color: '#fbbf24', fontWeight: 800, letterSpacing: '0.14em', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                  {t('academyPage.coursesLabel')}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#fafafa', letterSpacing: '-0.03em', fontSize: { xs: '1.8rem', md: '2.35rem' } }}>
                  {t('academyPage.startLearningTitle')}
                </Typography>
              </Box>
              <Button
                variant="text"
                endIcon={<ArrowRight size={15} />}
                onClick={() => navigate('/courses')}
                sx={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.875rem', '&:hover': { bgcolor: 'rgba(245,158,11,0.08)' } }}
              >
                {t('academyPage.allCoursesBtn')}
              </Button>
            </Box>
          </motion.div>

          {/* Category filter pills */}
          <Box
            sx={{
              display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, mb: 5,
              scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
              flexWrap: { xs: 'nowrap', md: 'wrap' },
            }}
          >
            {CATEGORIES.map((cat) => {
              const active = activeCategory === cat.value;
              return (
                <Chip
                  key={cat.value}
                  icon={cat.icon}
                  label={cat.label}
                  onClick={() => setActiveCategory(cat.value)}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    height: 34,
                    cursor: 'pointer',
                    transition: 'all 0.18s',
                    borderRadius: '8px',
                    border: '1px solid',
                    ...(active
                      ? { bgcolor: 'rgba(245,158,11,0.18)', borderColor: 'rgba(245,158,11,0.45)', color: '#fcd34d', '& .MuiChip-icon': { color: '#fcd34d' } }
                      : { bgcolor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)', color: '#94a3b8', '& .MuiChip-icon': { color: '#64748b' }, '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.15)', color: '#e2e8f0' } }
                    ),
                  }}
                />
              );
            })}
          </Box>

          <Box className="ac-course-grid">
              {coursesLoading
                ? Array(6).fill(0).map((_, i) => (
                    <Box key={i}>
                      <CourseCardSkeleton />
                    </Box>
                  ))
                : featured.length > 0
                ? featured.map((course, i) => (
                    <Box key={course._id || course.id || i}>
                      <CourseCard course={course} />
                    </Box>
                  ))
                : (
                    <Box sx={{ gridColumn: '1 / -1', py: 8, textAlign: 'center' }}>
                      <BookOpen size={40} color="#3f3f46" strokeWidth={1.5} />
                      <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>{t('academyPage.coursesBeingPrepared')}</Typography>
                      <Typography variant="caption" sx={{ color: '#475569', mt: 0.5, display: 'block' }}>
                        {t('academyPage.checkBackSoon')}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/courses')}
                        sx={{ mt: 3, borderColor: 'rgba(255,255,255,0.1)', color: '#94a3b8', borderRadius: '8px', '&:hover': { borderColor: 'rgba(245,158,11,0.4)', color: '#fbbf24', bgcolor: 'rgba(245,158,11,0.06)' } }}
                      >
                        {t('academyPage.browseAllCoursesBtn')}
                      </Button>
                    </Box>
                  )
              }
          </Box>
        </Container>
      </Box>

      {/* ── WHY QOREACADEMY ──────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }}>
            <Box sx={{ mb: 7 }}>
              <Typography variant="overline" sx={{ color: '#fbbf24', fontWeight: 800, letterSpacing: '0.14em', fontSize: '0.72rem', display: 'block', mb: 1 }}>
                {t('academyPage.whyDifferentLabel')}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: '#fafafa', letterSpacing: '-0.03em', fontSize: { xs: '1.8rem', md: '2.35rem' } }}>
                {t('academyPage.builtForEngineers')}
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={2.5}>
            {[
              {
                icon: <GraduationCap size={28} />,
                title: t('academyPage.expertCurriculumTitle'),
                desc: t('academyPage.expertCurriculumDesc'),
                color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',
              },
              {
                icon: <Brain size={28} />,
                title: t('academyPage.aiTeachesTitle'),
                desc: t('academyPage.aiTeachesDesc'),
                color: '#fb7185', bg: 'rgba(244,63,94,0.12)',
              },
              {
                icon: <Award size={28} />,
                title: t('academyPage.certCountTitle'),
                desc: t('academyPage.certCountDesc'),
                color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',
              },
              {
                icon: <FlaskConical size={28} />,
                title: t('academyPage.handsOnTitle'),
                desc: t('academyPage.handsOnDesc'),
                color: '#10b981', bg: 'rgba(16,185,129,0.1)',
              },
            ].map((card, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%', p: 3.5, borderRadius: '14px',
                      bgcolor: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.07)',
                      transition: 'transform 0.2s, border-color 0.2s',
                      '&:hover': { transform: 'translateY(-3px)', borderColor: `${card.color}35` },
                    }}
                  >
                    <Box sx={{ width: 56, height: 56, borderRadius: '12px', bgcolor: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color, mb: 3 }}>
                      {card.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1rem', color: '#fafafa', lineHeight: 1.35 }}>{card.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#71717a', lineHeight: 1.7, fontSize: '0.875rem' }}>{card.desc}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 12, md: 16 } }}>
        <Container maxWidth="sm">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1,
                  px: 2, py: 0.75, borderRadius: 6, mb: 4,
                  border: '1px solid rgba(245,158,11,0.3)', bgcolor: 'rgba(245,158,11,0.08)',
                }}
              >
                <Rocket size={13} color="#fbbf24" />
                <Typography variant="caption" sx={{ color: '#fbbf24', fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.72rem' }}>
                  {t('academyPage.earlyAccessLabel')}
                </Typography>
              </Box>

              <Typography variant="h2" sx={{ color: '#fafafa', fontWeight: 900, mb: 2, letterSpacing: '-0.03em', fontSize: { xs: '2rem', md: '2.75rem' }, lineHeight: 1.15 }}>
                {t('academyPage.beAmongFirst')}
              </Typography>
              <Typography sx={{ color: '#71717a', mb: 5, fontSize: '1rem', lineHeight: 1.7 }}>
                {t('academyPage.launchDesc')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  endIcon={<ArrowRight size={17} />}
                  sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                    color: '#111',
                    py: 1.6, px: 4, fontSize: '0.95rem', fontWeight: 800,
                    borderRadius: '12px', boxShadow: 'none',
                    '&:hover': { background: 'linear-gradient(135deg, #fbbf24 0%, #f87171 100%)' },
                  }}
                >
                  {t('academyPage.joinEarlyAccessBtn')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/courses')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.12)', color: '#a1a1aa', py: 1.6, px: 4,
                    fontSize: '0.95rem', borderRadius: '10px', fontWeight: 600,
                    '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)', color: '#fafafa' },
                  }}
                >
                  {t('academyPage.browseCoursesBtn')}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: '#3f3f46', mt: 3, display: 'block' }}>
{t('academyPage.ctaNoCardNote')}
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default Academy;
